import type { Task, ValidTaskBaseTypes } from '@awork/features/task/models/task.model'
import type { Project } from '@awork/features/project/models/project.model'
import type { User } from '@awork/features/user/models/user.model'
import type { TaskList } from '@awork/features/task/models/task-list.model'
import { TaskList as TaskListModel } from '@awork/features/task/models/task-list.model'
import type { TaskViewList } from '@awork/features/task/models/task-view-list.model'
import type { WorkType } from '@awork/features/task/models/work-type.model'
import type { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import { WITHOUT_LIST_ID } from '@awork/features/task/models/task-list.model.base'

export type ValidTaskListUITypes =
  | 'list' // normal list for project tasks
  | 'privateprojectlist' // list view for project tasks in my tasks
  | 'privatelist' // list view for my private tasks
  | 'type' // lists per type
  | 'time' // lists per due date
  | 'user' // lists per user
  | 'project' // lists per project
  | 'company' // lists per company
  | 'search' // lists for search results
  | 'smart' // smart lists
  | 'others' // lists for private tasks which have been assigned to other users
  | 'entity' // entity tasks - not in use currently
  | 'projecttype' // lists per project type
  | 'upcoming' // lists per due date
  | 'nolist' // 'Without list'

export interface TaskListUIOptions {
  optimisticId: string
  collapsed: boolean
  addTaskExpanded: boolean
}

/**
 * The task list UI model, to be shown in the UI
 */
export class TaskListUI {
  id?: string
  listId: string // only used for the d&d in the UI
  name?: string
  order: number
  collapsed: boolean
  animationCollapsed: boolean
  tasks?: Task[] = []
  entity?: Project | User | TaskList | TaskViewList | WorkType
  entityId: string
  type: ValidTaskListUITypes
  updatedOn?: Date
  title?: string
  addTaskExpanded?: boolean
  isArchived?: boolean
  group?: string
  groupingOption?: GroupByOptions
  isNew?: boolean
  baseType?: ValidTaskBaseTypes
  shown?: boolean
  quickFilterSelected?: boolean

  /**
   * Task list name max length
   */
  readonly taskListNameMaxLength = TaskListModel.NAME_MAX_LENGTH

  constructor(taskList: Partial<TaskListUI>) {
    Object.assign(this, taskList)
  }

  /**
   * Creates a mock task list UI from a given list and options
   * @param {TaskList} taskList
   * @param {Task[]} [tasks=[]]
   * @param {TaskListUIOptions} options
   * @returns {TaskListUI}
   */
  static fromTaskList(taskList: TaskList, tasks: Task[] = [], options?: TaskListUIOptions): TaskListUI {
    return new TaskListUI({
      id: taskList.id,
      // If at the time we are generating the list we have a new list focused,
      // we need to maintain the optimisticId so we don't lose the focus of the component
      listId: options?.optimisticId || taskList.id,
      name: taskList.name,
      entity: taskList,
      type: taskList.id === WITHOUT_LIST_ID ? 'nolist' : 'list',
      baseType: 'projecttask',
      collapsed: options?.collapsed,
      addTaskExpanded: options?.collapsed,
      tasks,
      order: taskList.order
    })
  }

  /**
   * Gets the order of the last task in the list plus 1 (max order)
   * @returns {number}
   */
  getLastTaskOrder(): number {
    if (this.tasks?.length) {
      return Math.max(
        ...this.tasks.map(task => {
          const list = task.lists.find(l => l.id === this.entity.id)

          return list ? list.orderOfTask + 1 : 0
        }),
        0
      )
    } else {
      return 0
    }
  }

  /**
   * Determines if one task is being save
   * @returns {boolean}
   */
  get isSaving(): boolean {
    return this.tasks.some(task => !task.id || task.isSaving)
  }
}
